/**
 * sortStructure is used to sort the table in a report
 * based on structure property and field name.
 * example propery and fieldName is below
 *      -----------------------------------------------------
 *      |           |   structureProperty   |   fieldName   |
 *      -----------------------------------------------------
 *      |   ID01    |   structureSignings   |   SIGN_TYPE   |
 *      |   5D01    |   structureUnits      |   STRUNITKEY  |
 *      |   5C03    |   Roadway             |   ON_UNDER    |
 *      -----------------------------------------------------
 * @param {Array} structure - The array of objects to sort.
 * @param {string} structureProperty - The property name within each object.
 * @param {string} fieldName - The field to compare.
 * @param {boolean} [isAscending=true] - Whether to sort in ascending order (default) or descending order.
 * @return {Array} - The sorted array.
 */
export const sortStructure = (structure, structureProperty, fieldName, isAscending = true) => {
  return (
    structure?.[structureProperty]?.sort((a, b) => {
      const valueA = a[fieldName];
      const valueB = b[fieldName];
      const isNumericValue = isNumeric(valueA) && isNumeric(valueB);
      return compare(valueA, valueB, isNumericValue, isAscending);
    }) || []
  );
};

/**
 * Sorts an array of objects based on a specified field.
 *
 * The function determines whether the field values are numeric and uses the appropriate comparison logic.
 * It supports both ascending and descending order.
 *
 * @param {Object[]} arr - The array of objects to sort.
 * @param {string} property - The key in each object to sort by.
 * @param {boolean} [isAscending=true] - Whether to sort in ascending order. Defaults to `true`.
 * @returns {Object[]} A new sorted array, or an empty array if the input is null or undefined.
 *
 * @example
 * sortArray(data, 'field1', false); // Sorts by 'field1' in descending order
 */
export const sortArray = (arr, property, isAscending = true) => {
  if (!Array.isArray(arr)) {
    return;
  }
  return arr.sort((a, b) => {
    const valueA = a[property];
    const valueB = b[property];
    const isNumericValue = isNumeric(valueA) && isNumeric(valueB);
    return compare(valueA, valueB, isNumericValue, isAscending);
  });
};

/**
 * Sorts an array of objects by two properties with configurable sort orders.
 *
 * @param {Array<Object>} arr - The array to sort.
 * @param {string} property1 - The primary property to sort by.
 * @param {string} property2 - The secondary property to sort by.
 * @param {"asc"|"desc"} [order1="asc"] - Sort order for the primary property.
 * @param {"asc"|"desc"} [order2="asc"] - Sort order for the secondary property.
 */
export const sortByTwoProperties = (
  arr,
  primaryProperty,
  secondaryProperty,
  primaryPropertyOrder = "asc",
  secondaryPropertyOrder = "asc"
) => {
  if (!Array.isArray(arr)) {
    return;
  }

  const isAscendingPrimaryProperty = primaryPropertyOrder === "asc";
  const isAscendingSecondaryProperty = secondaryPropertyOrder === "asc";

  arr.sort((a, b) => {
    const aPrimaryProperty = a[primaryProperty];
    const bPrimaryProperty = b[primaryProperty];
    const aSecondaryProperty = a[secondaryProperty];
    const bSecondaryProperty = b[secondaryProperty];

    const isNumericPrimaryProperty = isNumeric(aPrimaryProperty) && isNumeric(bPrimaryProperty);
    const isNumericSecondaryProperty = isNumeric(aSecondaryProperty) && isNumeric(bSecondaryProperty);

    const primaryComparison = compare(
      aPrimaryProperty,
      bPrimaryProperty,
      isNumericPrimaryProperty,
      isAscendingPrimaryProperty
    );
    const secondaryComparison = compare(
      aSecondaryProperty,
      bSecondaryProperty,
      isNumericSecondaryProperty,
      isAscendingSecondaryProperty
    );

    if (primaryComparison === 0) {
      return secondaryComparison;
    }
    return primaryComparison;
  });
};

/**
 * Compares two values and returns:
 * -1 if `a` is less than `b` (or greater if `isAscending` is false)
 *  1 if `a` is greater than `b` (or less if `isAscending` is false)
 *  0 if `a` is equal to `b`
 *
 * @param {*} a - The first value to compare.
 * @param {*} b - The second value to compare.
 * @param {boolean} isAscending - Whether to sort in ascending order (true) or descending order (false).
 * @return {number} - The comparison result (-1, 0, or 1).
 */
function compareNumber(a, b, isAscending) {
  if (a < b) {
    return isAscending ? -1 : 1;
  } else if (a > b) {
    return isAscending ? 1 : -1;
  }
  return 0;
}

/**
 * Compares two values based on type and sort order.
 *
 * @param {*} x - The first value to compare.
 * @param {*} y - The second value to compare.
 * @param {boolean} isNumeric - Indicates whether the values should be treated as numbers.
 * @param {boolean} isAscending - The sort order: "asc" for ascending, "desc" for descending.
 * @returns {number} A negative number if x < y, zero if x === y, or a positive number if x > y.
 *
 * @throws {Error} If `compareNumber` is not defined or fails.
 */
function compare(x, y, isNumeric, isAscending) {
  if (isNumeric) {
    return compareNumber(+x, +y, isAscending);
  }
  return isAscending ? String(x).localeCompare(String(y)) : String(y).localeCompare(String(x));
}

/**
 * Determines whether a given value is numeric.
 *
 * This function checks if the input is not `null`, `undefined`, or a boolean,
 * and whether it can be safely converted to a finite number.
 * Accepts: "42", "3.14", 42, 3.14
 * Rejects: "abc", true, null, undefined, NaN, Infinity
 * @param {*} value - The value to test for numeric validity.
 * @returns {boolean} `true` if the value is numeric and finite; otherwise, `false`.
 */
function isNumeric(value) {
  if (typeof value === "boolean" || value === null || value === undefined) {
    return false;
  }
  const num = Number(value);
  return !Number.isNaN(num) && Number.isFinite(num);
}
